import { SCHEMA_VERSION, field, fields } from "../content/model";
import type { ContentField, EntityType, FieldType } from "../content/model";
import { REGISTRY_VERSION } from "./registry";
import { specKey } from "./spec";
import type { Producer, SpecNode, ViewContext, ViewSpec } from "./spec";
import { validateSpec } from "./validate";
import AUTHORED from "./specs";

// WHERE A VIEW COMES FROM. Three producers, one interface, and the renderer cannot tell them
// apart.
//
//   authored   hand-written JSON for the canonical teacher screens. Wins when present, because
//              somebody decided this arrangement on purpose.
//   cached     whatever was resolved for this key already, including a generated spec once that
//              producer exists. Keyed on the content schema AND registry versions, so a spec
//              cannot outlive the things it binds to.
//   default    derived from the content schema. Always available, for any entity, including ones
//              nobody has written a view for.
//
// The default is not a fallback for emergencies; it is the thing that makes the content model the
// source of truth. Add a field to `content_schema.json` and it appears on screen with no spec
// written, no component chosen and no renderer touched. Any arrangement where a new field needs
// a second edit somewhere else will drift, and the field that never got its second edit is
// invisible rather than wrong, which is worse.

const CACHE = new Map<string, ViewSpec>();

/** What the default producer draws for each type of field.
 *
 *  This map is the only place that decides how a KIND of content is shown. It is presentation,
 *  which is why it lives here and not in the content schema: the schema says a field is prose,
 *  this says prose is drawn with Prose. A second product could map the same schema differently. */
const COMPONENT_FOR: Record<FieldType, string> = {
  text: "Fact",
  prose: "Prose",
  number: "Fact",
  text_list: "SourceList",
  standards: "StandardsList",
  blocks: "BlockContent",
  resources: "ResourceList",
  handle: "Fact",
};

function node(f: ContentField, entity: EntityType): SpecNode {
  const bind = { $bind: `${entity}.${f.name}` };
  const only = { present: `${entity}.${f.name}` };
  switch (COMPONENT_FOR[f.type]) {
    case "StandardsList":
      return { component: "StandardsList", $when: only,
               props: { label: f.label, standards: bind } };
    case "BlockContent":
      return { component: "BlockContent", $when: only, props: { label: f.label, blocks: bind } };
    case "SourceList":
      return { component: "SourceList", $when: only, props: { label: f.label, texts: bind } };
    case "ResourceList":
      return { component: "ResourceList", $when: only,
               props: { label: f.label, resources: bind } };
    case "Prose":
      return { component: "Prose", $when: only, props: { label: f.label, text: bind } };
    default:
      return { component: "Fact", $when: only,
               props: { label: f.label, value: bind, mono: f.type === "handle" } };
  }
}

/** A view of any unit or lesson, built from the schema alone.
 *
 *  Plain on purpose. It is not trying to be the authored screen — it is trying to show everything
 *  the entity has, in the order the schema declares it, so that what a teacher sees and what the
 *  content model holds cannot silently diverge. When it looks worse than the authored view, that
 *  is the authored view earning its place. */
export function defaultSpec(ctx: ViewContext): ViewSpec {
  const entity = ctx.entityType;
  // The header takes the question and the summary; everything else is a field in the body, in
  // the order the schema declares. Append a field there and it appears at the end of this.
  const body: SpecNode[] = fields(entity)
    .filter((f) => !["question", "summary"].includes(f.name))
    .map((f) => node(f, entity));

  return {
    id: `${entity}.${ctx.intent}.${ctx.audience}.default`,
    producer: "default",
    createdAt: new Date(0).toISOString(),   // derived, not authored: a timestamp would make two
                                            // identical specs compare unequal for no reason
    schemaVersion: SCHEMA_VERSION,
    registryVersion: REGISTRY_VERSION,
    entityType: entity,
    audience: ctx.audience,
    intent: ctx.intent,
    root: {
      component: "Page",
      children: [
        {
          component: "EntityHeader",
          // ONLY WHAT THIS KIND OF THING HAS. A lesson carries no essential question, and a
          // producer that bound one anyway would emit a spec its own validator rejects — which
          // is the check working, and a reason to ask the schema rather than assume.
          props: {
            course: { $bind: "context.courseName" },
            within: { $bind: "context.unitTitle" },
            title: { $bind: `${entity}.title` },
            ...(field(entity, "question") ? { question: { $bind: `${entity}.question` } } : {}),
            ...(field(entity, "summary") ? { summary: { $bind: `${entity}.summary` } } : {}),
          },
        },
        { component: "Fields", children: body },
      ],
    },
  };
}

/** The spec a human wrote for this context, if there is one. */
export function authoredSpec(ctx: ViewContext): ViewSpec | null {
  return AUTHORED[specKey(ctx, REGISTRY_VERSION)] ?? null;
}

/** THE SEAM. A model-produced spec for this context, once there is a model producing them.
 *
 *  Not implemented, and deliberately not stubbed with a comment saying "plug in here": a seam
 *  nobody has passed anything through is a promise, not an interface. `produce.test.ts` swaps a
 *  working fake in here and asserts it validates, renders, caches and is logged as `generated` —
 *  so what plugs in later is a function with a signature that has been exercised.
 *
 *  When it arrives it will be asynchronous and server-side: the model credentials, the cost
 *  control and the cache that makes it affordable all belong on the API, and this becomes a fetch
 *  of a spec the server produced and validated. The shape below is what that fetch resolves to. */
export type GeneratedProducer = (ctx: ViewContext) => ViewSpec | null;

let generated: GeneratedProducer | null = null;

/** Register a generated producer. Exported for the test that proves the seam is real. */
export function setGeneratedProducer(p: GeneratedProducer | null): void {
  generated = p;
  CACHE.clear();
}

export interface Resolved {
  spec: ViewSpec;
  producer: Producer;
  /** Errors that made us fall back. Empty on the happy path; shown by the debug page. */
  errors: { at: string; message: string }[];
  cached: boolean;
}

/** The one entry point every unit and lesson screen goes through.
 *
 *  Screens ask for a view by CONTEXT — what kind of thing, for whom, to what end — and get back
 *  something to render. They never name a producer, never read a spec file, and never know
 *  whether a model was involved. That is what makes the generated producer a change to this
 *  function rather than a change to every screen. */
export function resolveView(ctx: ViewContext): Resolved {
  const key = specKey(ctx, REGISTRY_VERSION);
  const errors: { at: string; message: string }[] = [];

  const cachedSpec = CACHE.get(key);
  if (cachedSpec) return { spec: cachedSpec, producer: cachedSpec.producer, errors, cached: true };

  for (const candidate of [authoredSpec(ctx), generated?.(ctx) ?? null]) {
    if (!candidate) continue;
    const found = validateSpec(candidate);
    if (found.length === 0) {
      CACHE.set(key, candidate);
      log(key, candidate.producer, false);
      return { spec: candidate, producer: candidate.producer, errors, cached: false };
    }
    // LOUD, and then the default. An invalid spec is a bug in whoever produced it, and a screen
    // that quietly omitted the invalid parts would hide it for as long as nobody noticed the
    // missing standards.
    errors.push(...found);
    console.error(`[spec] ${key} from ${candidate.producer} failed validation:`,
                  found.map((e) => `${e.at}: ${e.message}`));
  }

  const fallback = defaultSpec(ctx);
  const bad = validateSpec(fallback);
  if (bad.length > 0) {
    // The default producing an invalid spec means the schema and the registry disagree — a field
    // type with no component, a component whose props changed. Nothing renders correctly from
    // here, so say exactly that.
    errors.push(...bad);
    console.error(`[spec] the DEFAULT spec for ${key} is invalid — the content schema and the `
                  + `component registry disagree:`, bad.map((e) => `${e.at}: ${e.message}`));
  }
  CACHE.set(key, fallback);
  log(key, "default", false);
  return { spec: fallback, producer: "default", errors, cached: false };
}

function log(key: string, producer: Producer, cached: boolean): void {
  console.debug(`[spec] ${key} ← ${producer}${cached ? " (cached)" : ""}`);
}

/** For tests and the debug page, which need to see resolution happen rather than a cache hit. */
export function clearSpecCache(): void {
  CACHE.clear();
}
